import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Modal, Form, Input, Toast, Popconfirm } from '@douyinfe/semi-ui';
import { IconEdit, IconDelete, IconPlus, IconSearch } from '@douyinfe/semi-icons';
import request from '../../utils/request';
import styles from './index.module.scss';

const DomainBind = () => {
    const [loading, setLoading] = useState(false);
    const [dataSource, setDataSource] = useState([]);
    const [pagination, setPagination] = useState({ currentPage: 1, pageSize: 10, total: 0 });
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [currentRecord, setCurrentRecord] = useState(null);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [searchKey, setSearchKey] = useState('');

    const fetchList = async (page = 1, limit = 10) => {
        setLoading(true);
        try {
            const response = await request.get('/go/sp/sp/list_domain_bind/', {
                params: {
                    page: page - 1,
                    limit,
                    key: searchKey
                }
            });
            if (response.errno === 0) {
                setDataSource(response.list || []);
                setPagination(prev => ({
                    ...prev,
                    currentPage: page,
                    total: response.total_cnt
                }));
            } else {
                Toast.error(response.errstr || '获取列表失败');
            }
        } catch (error) {
            console.error('获取列表失败:', error);
            Toast.error('获取列表失败');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchList();
    }, []);

    const handleAdd = async (values) => {
        try {
            const response = await request.post('/go/sp/sp/add_domain_bind/', values);
            if (response.errno === 0) {
                Toast.success('添加成功');
                setShowAddModal(false);
                fetchList();
            } else {
                Toast.error(response.errstr || '添加失败');
            }
        } catch (error) {
            console.error('添加失败:', error);
            Toast.error('添加失败');
        }
    };

    const handleEdit = async (values) => {
        try {
            const response = await request.post('/go/sp/sp/edit_domain_bind/', {
                ...values,
                uid: currentRecord.uid
            });
            if (response.errno === 0) {
                Toast.success('编辑成功');
                setShowEditModal(false);
                fetchList();
            } else {
                Toast.error(response.errstr || '编辑失败');
            }
        } catch (error) {
            console.error('编辑失败:', error);
            Toast.error('编辑失败');
        }
    };

    const handleDelete = async (uids) => {
        try {
            const response = await request.post('/go/sp/sp/delete_domain_bind/', {
                uids: Array.isArray(uids) ? uids.join(',') : uids
            });
            if (response.errno === 0) {
                Toast.success('删除成功');
                setSelectedRowKeys([]);
                fetchList();
            } else {
                Toast.error(response.errstr || '删除失败');
            }
        } catch (error) {
            console.error('删除失败:', error);
            Toast.error('删除失败');
        }
    };

    const formatTimestamp = (timestamp) => {
        if (!timestamp) return '-';
        const date = new Date(parseInt(timestamp));
        return date.toLocaleString();
    };

    const columns = [
        {
            title: '域名',
            dataIndex: 'domain',
            width: 200,
            render: (text, record) => (
                <div style={{ fontSize: '12px' }}>
                    <div style={{ color: 'var(--semi-color-text-0)', marginBottom: '4px' }}>{text}</div>
                    <div style={{ color: 'var(--semi-color-text-2)' }}>{record.name || '-'}</div>
                </div>
            )
        },
        {
            title: '绑定地址',
            dataIndex: 'bind',
            width: 200
        },
        {
            title: '创建时间',
            dataIndex: 'create_time',
            width: 180,
            render: (text) => formatTimestamp(text)
        },
        {
            title: '操作',
            width: 200,
            render: (_, record) => (
                <Space>
                    <Button
                        type='primary'
                        icon={<IconEdit />}
                        onClick={() => {
                            setCurrentRecord(record);
                            setShowEditModal(true);
                        }}
                    >
                        编辑
                    </Button>
                    <Popconfirm
                        title="确定要删除该域名吗？"
                        onConfirm={() => handleDelete(record.uid)}
                    >
                        <Button type='danger' icon={<IconDelete />}>删除</Button>
                    </Popconfirm>
                </Space>
            )
        }
    ];

    return (
        <div className={styles.domainBindContainer}>
            <div className={styles.header}>
                <Space>
                    <Input
                        placeholder="搜索域名/备注"
                        value={searchKey}
                        onChange={setSearchKey}
                        style={{ width: 200 }}
                        showClear
                    />
                    <Button
                        type="primary"
                        onClick={() => fetchList(1, pagination.pageSize)}
                        loading={loading}
                        icon={<IconSearch />}
                    >
                        搜索
                    </Button>
                    <Button type='primary' icon={<IconPlus />} onClick={() => setShowAddModal(true)}>新增域名</Button>
                    {selectedRowKeys.length > 0 && (
                        <Popconfirm
                            title={`确定要删除选中的 ${selectedRowKeys.length} 个域名吗？`}
                            onConfirm={() => handleDelete(selectedRowKeys)}
                        >
                            <Button type='danger' icon={<IconDelete />}>批量删除</Button>
                        </Popconfirm>
                    )}
                </Space>
            </div>

            <Table
                loading={loading}
                columns={columns}
                dataSource={dataSource}
                pagination={{
                    currentPage: pagination.currentPage,
                    pageSize: pagination.pageSize,
                    total: pagination.total,
                    onPageChange: (page) => fetchList(page, pagination.pageSize),
                    onPageSizeChange: (size) => fetchList(1, size)
                }}
                rowKey="uid"
                rowSelection={{
                    selectedRowKeys,
                    onChange: setSelectedRowKeys
                }}
            />

            <Modal
                title="新增域名"
                visible={showAddModal}
                onCancel={() => setShowAddModal(false)}
                footer={null}
                centered
            >
                <Form onSubmit={handleAdd}>
                    <Form.Input
                        field="domain"
                        label="域名"
                        placeholder="请输入完整域名"
                        rules={[{ required: true, message: '请输入域名' }]}
                    />
                    <Form.Input
                        field="name"
                        label="备注"
                        placeholder="请输入备注"
                    />
                    <Form.Input
                        field="bind"
                        label="绑定地址"
                        placeholder="请输入绑定地址，如：site.index.index"
                    />
                    <div style={{ marginTop: 24, textAlign: 'right', paddingBottom: 24 }}>
                        <Space>
                            <Button type='tertiary' onClick={() => setShowAddModal(false)}>取消</Button>
                            <Button type='primary' htmlType='submit'>确定</Button>
                        </Space>
                    </div>
                </Form>
            </Modal>

            <Modal
                title="编辑域名"
                visible={showEditModal}
                onCancel={() => setShowEditModal(false)}
                footer={null}
                centered
            >
                <Form
                    onSubmit={handleEdit}
                    initValues={{
                        domain: currentRecord?.domain,
                        name: currentRecord?.name,
                        bind: currentRecord?.bind
                    }}
                >
                    <Form.Input
                        field="domain"
                        label="域名"
                        placeholder="请输入完整域名"
                        rules={[{ required: true, message: '请输入域名' }]}
                    />
                    <Form.Input
                        field="name"
                        label="备注"
                        placeholder="请输入备注"
                    />
                    <Form.Input
                        field="bind"
                        label="绑定地址"
                        placeholder="请输入绑定地址，如：site.index.index"
                    />
                    <div style={{ marginTop: 24, textAlign: 'right', paddingBottom: 24 }}>
                        <Space>
                            <Button type='tertiary' onClick={() => setShowEditModal(false)}>取消</Button>
                            <Button type='primary' htmlType='submit'>确定</Button>
                        </Space>
                    </div>
                </Form>
            </Modal>
        </div>
    );
};

export default DomainBind;